'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import PetHealthChart from './PetHealthChart';

type ProfileTab = 'about' | 'health' | 'gallery' | 'diary';

const vaccinations = [
{ id: 'vax-001', name: 'Rabies Vaccine', date: 'Jan 15, 2026', vet: 'Dr. Ana Reyes', status: 'done', next: 'Jan 15, 2027' },
{ id: 'vax-002', name: 'DHPP (Distemper/Parvo)', date: 'Nov 20, 2025', vet: 'Dr. Ana Reyes', status: 'done', next: 'Nov 20, 2026' },
{ id: 'vax-003', name: 'Bordetella', date: 'Mar 5, 2026', vet: 'Dr. Miguel Santos', status: 'done', next: 'Mar 5, 2027' },
{ id: 'vax-004', name: 'Annual Check-up', date: null, vet: null, status: 'upcoming', next: 'Aug 30, 2026' },
{ id: 'vax-005', name: 'Leptospirosis Booster', date: null, vet: null, status: 'upcoming', next: 'Sep 12, 2026' }];


const diaryEntries = [
{
  id: 'diary-001',
  date: 'Jul 25, 2026',
  type: 'milestone',
  title: '🎂 Luna\'s 3rd Birthday!',
  body: 'Celebrated Luna\'s birthday with a special homemade peanut butter cake. She loved every bite! 🐾',
  image: "https://images.unsplash.com/photo-1647277121140-0281898ab9bb",
  imageAlt: 'Small dog in front of birthday cake with candles'
},
{
  id: 'diary-002',
  date: 'Jul 18, 2026',
  type: 'health',
  title: '💉 Rabies Booster Done',
  body: 'Quick visit to Petville Vet. Dr. Reyes said Luna is in perfect health. Weight: 4.2kg. 👏',
  image: null
},
{
  id: 'diary-003',
  date: 'Jul 10, 2026',
  type: 'adventure',
  title: '🌳 First Trip to BGC Park',
  body: 'Luna made 3 new dog friends at the park today! She ran around for 2 hours straight. Exhausted but so happy.',
  image: "https://images.unsplash.com/photo-1674822557258-581f5dbd1613",
  imageAlt: 'Shih Tzu dog playing in park with other dogs'
},
{
  id: 'diary-004',
  date: 'Jun 28, 2026',
  type: 'grooming',
  title: '✂️ Monthly Grooming Session',
  body: 'Got the summer cut at Groom & Bloom in Makati. She looks like a little cloud! ☁️',
  image: null
}];


const galleryImages = [
{ id: 'gal-001', src: "https://images.unsplash.com/photo-1614971988996-8efb9c213478", alt: 'Orange tabby cat lying on couch looking at camera' },
{ id: 'gal-002', src: "https://images.unsplash.com/photo-1629756519831-672b3f341dd2", alt: 'Shih Tzu dog with brown and white fur sitting on grass' },
{ id: 'gal-003', src: "https://images.unsplash.com/photo-1712681342400-59193f6aab03", alt: 'Golden retriever puppy sitting outdoors in sunlight' },
{ id: 'gal-004', src: "https://images.unsplash.com/photo-1614108116787-57ca473977f9", alt: 'Siamese cat with blue eyes looking sideways' },
{ id: 'gal-005', src: "https://images.unsplash.com/photo-1532512855354-6f4db70cd4fd", alt: 'Gray British shorthair cat sitting on white surface' },
{ id: 'gal-006', src: "https://images.unsplash.com/photo-1711551203322-7edc5ce0da98", alt: 'Dog wearing collar outdoors in garden setting' }];


export default function PetProfileShell() {
  const [activeTab, setActiveTab] = useState<ProfileTab>('about');
  const [following, setFollowing] = useState(false);

  const tabs: {id: ProfileTab;label: string;}[] = [
  { id: 'about', label: 'About' },
  { id: 'health', label: 'Health' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'diary', label: 'Diary' }];


  return (
    <div className="min-h-screen bg-muted">
      {/* Cover photo */}
      <div className="relative h-[220px] lg:h-[280px] bg-gradient-to-br from-primary-tint to-secondary-tint overflow-hidden">
        <AppImage
          src="https://images.unsplash.com/photo-1721035557731-ff09f008d91e"
          alt="Warm editorial pet photography background with soft bokeh lighting"
          fill
          className="object-cover opacity-50"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-t from-muted/60 to-transparent"></div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 lg:px-10 -mt-16 pb-12">
        {/* Profile header card */}
        <div className="bg-white rounded-2xl border border-border/60 shadow-card p-6 mb-5">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Avatar */}
            <div className="relative flex-shrink-0 -mt-16 sm:-mt-20">
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-float bg-muted">
                <AppImage
                  src="https://images.unsplash.com/photo-1611786896477-2e4a99745038"
                  alt="Luna the Shih Tzu — white and brown fluffy dog with big eyes"
                  width={128}
                  height={128}
                  className="object-cover w-full h-full" />
                
              </div>
              {/* NFC active indicator */}
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full flex items-center justify-center border-2 border-white shadow-card">
                <Icon name="WifiIcon" size={14} className="text-white" />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pt-2 sm:pt-0">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-extrabold text-foreground">Luna</h1>
                    <span className="bg-primary-tint text-primary text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Icon name="WifiIcon" size={11} />
                      NFC Protected
                    </span>
                  </div>
                  <p className="text-sm text-body-text font-medium mt-1">
                    Shih Tzu · Female · 3 years old · BGC, Taguig
                  </p>

                  {/* PH ID */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center gap-1.5 bg-navy/5 border border-navy/10 rounded-lg px-3 py-1.5">
                      <Icon name="IdentificationIcon" size={14} className="text-navy" />
                      <span className="text-xs font-extrabold text-navy font-tabular">PH-ID: 2024-00847</span>
                    </div>
                    <button className="p-1.5 hover:bg-muted rounded-lg transition-colors" aria-label="Copy PH ID">
                      <Icon name="ClipboardDocumentIcon" size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setFollowing(!following)}
                    className={following ? 'btn-outline text-sm' : 'btn-primary text-sm'}>
                    
                    {following ? 'Following' : 'Follow'}
                  </button>
                  <button className="btn-outline text-sm">
                    <Icon name="ChatBubbleLeftIcon" size={15} />
                    Message Owner
                  </button>
                  <button className="p-2.5 rounded-full border border-border hover:bg-muted transition-colors" aria-label="Share profile">
                    <Icon name="ShareIcon" size={16} className="text-body-text" />
                  </button>
                </div>
              </div>

              {/* Stats row */}
              <div className="flex gap-6 mt-4 pt-4 border-t border-border">
                {[
                { v: '284', l: 'Followers' },
                { v: '47', l: 'Furfriends' },
                { v: '12', l: 'Awards' },
                { v: '38', l: 'Diary Entries' }].
                map((s) =>
                <div key={`profile-stat-${s.l}`} className="text-center">
                    <p className="text-lg font-extrabold text-foreground font-tabular">{s.v}</p>
                    <p className="text-xs text-muted-foreground font-medium">{s.l}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="bg-white rounded-xl border border-border/60 shadow-card mb-5 overflow-x-auto scrollbar-hide">
          <div className="flex">
            {tabs.map((tab) =>
            <button
              key={`profile-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3.5 text-sm font-bold transition-colors relative whitespace-nowrap px-4 ${
              activeTab === tab.id ?
              'text-primary' : 'text-muted-foreground hover:text-foreground'}`
              }>
              
                {tab.label}
                {activeTab === tab.id &&
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"></div>
              }
              </button>
            )}
          </div>
        </div>

        {/* Tab content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
          {/* Main content */}
          <div>
            {/* ABOUT TAB */}
            {activeTab === 'about' &&
            <div className="flex flex-col gap-5 fade-in">
                {/* Fact tiles */}
                <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
                  <h3 className="text-sm font-bold text-foreground mb-4">About Luna</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                  { icon: '🎂', label: 'Birthday', value: 'July 25, 2023' },
                  { icon: '⚖️', label: 'Weight', value: '4.2 kg' },
                  { icon: '🍗', label: 'Favorite Food', value: 'Chicken & Rice' },
                  { icon: '🧸', label: 'Favorite Toy', value: 'Squeaky Duck' },
                  { icon: '🏠', label: 'Home', value: 'BGC, Taguig' },
                  { icon: '👩‍⚕️', label: 'Veterinarian', value: 'Dr. Ana Reyes' }].
                  map((fact) =>
                  <div key={`fact-${fact.label}`} className="bg-muted rounded-xl p-3.5">
                        <span className="text-lg">{fact.icon}</span>
                        <p className="text-xs text-muted-foreground font-semibold mt-1">{fact.label}</p>
                        <p className="text-sm font-bold text-foreground mt-0.5">{fact.value}</p>
                      </div>
                  )}
                  </div>

                  {/* Personality chips */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-bold text-muted-foreground mb-2.5">PERSONALITY</p>
                    <div className="flex flex-wrap gap-2">
                      {['Playful 🎾', 'Affectionate 💕', 'Curious 🔍', 'Social 🐾', 'Gentle 🌸'].map((trait) =>
                    <span key={`trait-${trait}`} className="chip text-xs">{trait}</span>
                    )}
                    </div>
                  </div>
                </div>

                {/* NFC Tag status card */}
                <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-foreground">NFC Tag Status</h3>
                    <span className="badge-verified">✓ Active</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-primary-tint rounded-xl p-3.5">
                      <p className="text-xs text-muted-foreground font-semibold">Tag ID</p>
                      <p className="text-sm font-extrabold text-primary font-tabular">NFC-PH-00847</p>
                    </div>
                    <div className="bg-secondary-tint rounded-xl p-3.5">
                      <p className="text-xs text-muted-foreground font-semibold">Total Scans</p>
                      <p className="text-sm font-extrabold text-secondary font-tabular">23 scans</p>
                    </div>
                    <div className="bg-muted rounded-xl p-3.5">
                      <p className="text-xs text-muted-foreground font-semibold">Last Scanned</p>
                      <p className="text-sm font-bold text-foreground">Jul 20, 2026</p>
                    </div>
                    <div className="bg-muted rounded-xl p-3.5">
                      <p className="text-xs text-muted-foreground font-semibold">Lost Mode</p>
                      <p className="text-sm font-bold text-secondary">OFF — Safe 🏠</p>
                    </div>
                  </div>
                  <a
                  href="/nfc-tag-scan-lost-mode-view"
                  className="mt-4 flex items-center gap-2 text-sm font-bold text-primary hover:text-primary-hover transition-colors no-underline">
                  
                    <Icon name="QrCodeIcon" size={16} />
                    View Public Tag Page
                    <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                  </a>
                </div>
              </div>
            }

            {/* HEALTH TAB */}
            {activeTab === 'health' &&
            <div className="flex flex-col gap-5 fade-in">
                {/* Weight chart */}
                <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-foreground">Weight Tracking</h3>
                    <span className="text-xs text-secondary font-bold bg-secondary-tint px-2 py-1 rounded-full">Healthy range ✓</span>
                  </div>
                  <PetHealthChart />
                </div>

                {/* Vaccination timeline */}
                <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-foreground">Health Records</h3>
                    <button className="btn-secondary text-xs px-3 py-1.5">
                      <Icon name="PlusIcon" size={13} />
                      Add Record
                    </button>
                  </div>

                  <div className="flex flex-col gap-0 relative">
                    {/* Timeline line */}
                    <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-border"></div>

                    {vaccinations.map((vax, idx) =>
                  <div key={vax.id} className="relative flex gap-4 pb-5 last:pb-0">
                        {/* Dot */}
                        <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    vax.status === 'done' ?
                    'bg-secondary' : 'bg-accent border-2 border-white shadow-card'}`
                    }>
                          {vax.status === 'done' ?
                      <Icon name="CheckIcon" size={12} className="text-white" /> :

                      <Icon name="ClockIcon" size={12} className="text-white" />
                      }
                        </div>

                        {/* Content */}
                        <div className={`flex-1 rounded-xl p-3.5 ${
                    vax.status === 'upcoming' ? 'bg-accent-tint border border-accent/20' : 'bg-muted'}`
                    }>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-bold text-foreground">{vax.name}</p>
                              {vax.date &&
                          <p className="text-xs text-muted-foreground font-medium mt-0.5">
                                  Done: {vax.date} · {vax.vet}
                                </p>
                          }
                            </div>
                            <div className="text-right flex-shrink-0">
                              {vax.status === 'upcoming' ?
                          <span className="badge-adoption text-xs">Due: {vax.next}</span> :

                          <span className="text-xs text-muted-foreground font-medium">Next: {vax.next}</span>
                          }
                            </div>
                          </div>
                          {vax.status === 'upcoming' &&
                      <button className="mt-2 text-xs font-bold text-accent-dark hover:text-accent transition-colors flex items-center gap-1">
                              <Icon name="BellIcon" size={12} />
                              Set reminder
                            </button>
                      }
                        </div>
                      </div>
                  )}
                  </div>
                </div>

                {/* Allergies & meds */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-border/60 shadow-card p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="ExclamationTriangleIcon" size={16} className="text-accent" />
                      <h3 className="text-sm font-bold text-foreground">Allergies</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['Chicken protein (mild)', 'Dust mites'].map((a) =>
                    <span key={`allergy-${a}`} className="bg-accent-tint text-accent-dark text-xs font-semibold px-3 py-1.5 rounded-full">{a}</span>
                    )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-border/60 shadow-card p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="BeakerIcon" size={16} className="text-primary" />
                      <h3 className="text-sm font-bold text-foreground">Current Medications</h3>
                    </div>
                    <div className="flex flex-col gap-2">
                      {[
                    { name: 'Nexgard Spectra', freq: 'Monthly · Tick prevention' },
                    { name: 'Omega-3 supplement', freq: 'Daily · Coat health' }].
                    map((med) =>
                    <div key={`med-${med.name}`} className="bg-primary-tint rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-primary">{med.name}</p>
                          <p className="text-xs text-muted-foreground font-medium">{med.freq}</p>
                        </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            }

            {/* GALLERY TAB */}
            {activeTab === 'gallery' &&
            <div className="bg-white rounded-xl border border-border/60 shadow-card p-5 fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-foreground">Luna's Gallery</h3>
                  <span className="text-xs text-muted-foreground font-medium">{galleryImages.length} photos</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {galleryImages.map((img) =>
                <div key={img.id} className="aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer group">
                      <AppImage
                    src={img.src}
                    alt={img.alt}
                    width={200}
                    height={200}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300" />
                  
                    </div>
                )}
                </div>
              </div>
            }

            {/* DIARY TAB */}
            {activeTab === 'diary' &&
            <div className="flex flex-col gap-4 fade-in">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">Luna's Diary</h3>
                  <button className="btn-primary text-xs px-4 py-2">
                    <Icon name="PlusIcon" size={13} />
                    Add Entry
                  </button>
                </div>

                {diaryEntries.map((entry) =>
              <div key={entry.id} className="bg-white rounded-xl border border-border/60 shadow-card overflow-hidden">
                    {entry.image &&
                <div className="h-[200px] bg-muted overflow-hidden">
                        <AppImage
                    src={entry.image}
                    alt={entry.imageAlt || ''}
                    width={600}
                    height={200}
                    className="object-cover w-full h-full" />
                  
                      </div>
                }
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    entry.type === 'milestone' ? 'bg-yellow text-navy' :
                    entry.type === 'health' ? 'bg-secondary-tint text-secondary' :
                    entry.type === 'adventure' ? 'bg-primary-tint text-primary' : 'bg-muted text-muted-foreground'}`
                    }>
                          {entry.type.charAt(0).toUpperCase() + entry.type.slice(1)}
                        </span>
                        <span className="text-xs text-muted-foreground font-medium">{entry.date}</span>
                      </div>
                      <h4 className="text-sm font-bold text-foreground mb-1">{entry.title}</h4>
                      <p className="text-sm text-body-text font-medium leading-relaxed">{entry.body}</p>
                    </div>
                  </div>
              )}
              </div>
            }
          </div>

          {/* Right sidebar */}
          <aside className="flex flex-col gap-4">
            {/* QR code card */}
            <div className="bg-white rounded-xl border border-border/60 shadow-card p-5 text-center">
              <h3 className="text-sm font-bold text-foreground mb-3">Share Luna's Profile</h3>
              <div className="w-32 h-32 mx-auto bg-foreground rounded-xl flex items-center justify-center mb-3">
                <div className="grid grid-cols-3 gap-1 p-3">
                  {Array.from({ length: 9 }).map((_, i) =>
                  <div
                    key={`qr-cell-${i}`}
                    className={`w-full aspect-square rounded-sm ${[0, 1, 2, 3, 5, 6, 7, 8].includes(i) ? 'bg-white' : 'bg-foreground'}`}>
                  </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-medium mb-3">Scan to view Luna's profile</p>
              <button className="btn-outline text-xs px-4 py-2 w-full justify-center">
                <Icon name="ArrowDownTrayIcon" size={14} />
                Download QR Code
              </button>
            </div>

            {/* Owner card */}
            <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">Furparent</h3>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <AppImage
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_19d49f311-1766940465931.png"
                    alt="Maria Santos — Filipino woman with short dark hair, Luna's owner"
                    width={44}
                    height={44}
                    className="object-cover w-full h-full" />
                  
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">Maria Santos</p>
                  <p className="text-xs text-muted-foreground font-medium">@mariasantos · BGC, Taguig</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button className="btn-primary text-xs flex-1 justify-center py-2">Follow</button>
                <button className="btn-outline text-xs flex-1 justify-center py-2">Message</button>
              </div>
            </div>

            {/* Furfriends */}
            <div className="bg-white rounded-xl border border-border/60 shadow-card p-5">
              <h3 className="text-sm font-bold text-foreground mb-3">Furfriends</h3>
              <div className="flex flex-wrap gap-2">
                {[
                { id: 'ff-001', name: 'Mochi', src: "https://images.unsplash.com/photo-1611579569810-6af0831bf22f", alt: 'Gray British shorthair cat face closeup' },
                { id: 'ff-002', name: 'Biscuit', src: "https://images.unsplash.com/photo-1567007992671-bf54249cd8aa", alt: 'Golden retriever puppy face closeup' },
                { id: 'ff-003', name: 'Waffle', src: "https://images.unsplash.com/photo-1489057205369-ff930a5157ca", alt: 'Corgi mix dog face closeup' }].
                map((ff) =>
                <div key={ff.id} className="flex flex-col items-center gap-1 cursor-pointer group">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-muted border-2 border-border group-hover:border-primary transition-colors">
                      <AppImage src={ff.src} alt={ff.alt} width={48} height={48} className="object-cover w-full h-full" />
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">{ff.name}</span>
                  </div>
                )}
                <div className="flex flex-col items-center gap-1 cursor-pointer">
                  <div className="w-12 h-12 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors">
                    <Icon name="PlusIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground font-medium">Add</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>);

}